/**
 * droid_lang.ts — DROIDSCRIPT 2 (2026-09-24): the language a droid is programmed in.
 *
 * MIRROR: byte-identical at `client/src/lib/droid_lang.ts` and `server/src/lib/droid_lang.ts`
 * (verify:droid-lang diffs them). Pure — no spacetimedb, no DOM. The client uses it to check a
 * program as you type, list the API and estimate the cost; the server compiles and RUNS it.
 *
 * EASY: one command per line, exactly like Droidscript 1 — every old program still runs:
 *     patrol 40
 *     scan life
 *     wait 20
 *     loop
 *
 * DEEPER when you want it: variables, maths, conditions, loops, functions, event handlers:
 *     let trips = 0
 *     forever
 *       goto extractor(300)          # a record with x/z walks to it
 *       collect
 *       if cargo_free() < 50
 *         goto structure(300)
 *         store all
 *       end
 *       trips += 1
 *       say "trip " + trips
 *     end
 *     on creature_near 25            # handlers interrupt the main program (first match wins)
 *       attack nearest
 *     end
 *     every 120
 *       advertise "Fresh ore at the Dust Road stall!"
 *     end
 *
 * Arguments: bare words are taken literally (`store metal`, `move n 40`); expressions work with
 * spaces around operators or commas (`goto home_x() + 20, home_z()`); wrap a variable in
 * parentheses to pass its value where a word is expected (`store (what)`).
 *
 * THE VM is a flat bytecode interpreter whose whole state is JSON (a droid keeps running across
 * server ticks). A command that takes time (walking, waiting, crafting) YIELDS a `wait` the host
 * advances; handlers interrupt a wait and the main program resumes where it stopped. Every
 * instruction, sensor read and action costs maintenance credits (droid_classes.ts — the COST LAW).
 */
import {
  DROID_CLASSES, droidStats, classHas, INSTR_COST, QUERY_COST, MOVE_COST_PER_M, UPKEEP_CALL_MAX, UPKEEP_HOUR_CAP,
  type DroidClass, type DroidCap,
} from './droid_classes';

// ─── Values ────────────────────────────────────────────────────────────────────────────────────

export type Val = number | string | boolean | null | Val[] | { [k: string]: Val };

export const LIMITS = {
  lines: 400, source: 16000, instr: 6000, stack: 64, frames: 16, vars: 120,
  list: 64, str: 200, stepsPerTick: 400, handlers: 12, funcs: 32,
  /** Items in one value, nested lists counted in full (2026-09-28): `forever: a = [a, a]` doubled a SHARED list 67 times
   *  in one tick — a few bytes in memory, 2^67 items as JSON, and the server's JSON.stringify of the brain state never
   *  returned (the droid clock, and with it the module, stalled). */
  cells: 4096,
};

export class DroidError extends Error {}

/** A craft costs its base price plus this much per point of the design's complexity. */
export const CRAFT_PER_COMPLEXITY = 1.5;

// ─── Registries: the API ───────────────────────────────────────────────────────────────────────

export type PKind = 'num' | 'word' | 'any' | 'text' | 'nums';

export interface ParamSpec {
  name: string;
  kind: PKind;
  opt?: boolean;
  min?: number;
  max?: number;
  /** A word param's allowed values (null = any word). */
  words?: string[];
}

export interface CmdSpec {
  name: string;
  group: string;
  params: ParamSpec[];
  cap: DroidCap;
  /** Base price per use (credits, before the class multiplier). */
  cost: number;
  /** Typical duration (s) — also the minimum a non-moving command occupies. 'move' = distance-driven. */
  secs: number | 'move';
  doc: string;
  example: string;
}

const DIRS = ['n', 's', 'e', 'w', 'north', 'south', 'east', 'west', 'ne', 'nw', 'se', 'sw'];

const C = (name: string, group: string, cap: DroidCap, cost: number, secs: number | 'move', params: ParamSpec[], doc: string, example: string): CmdSpec =>
  ({ name, group, cap, cost, secs, params, doc, example });

export const COMMANDS: Record<string, CmdSpec> = {};
for (const c of [
  // movement
  C('goto', 'Movement', 'move', 0, 'move', [{ name: 'x', kind: 'any' }, { name: 'z', kind: 'num', opt: true }], 'Walk to a point (x z), or to anything with x/z — a creature, an extractor, a structure.', 'goto 120 -40   ·   goto extractor(300)'),
  C('move', 'Movement', 'move', 0, 'move', [{ name: 'dir', kind: 'word', words: DIRS }, { name: 'metres', kind: 'num', min: 1, max: 400 }], 'Walk a relative leg from where the droid stands.', 'move n 40'),
  C('route', 'Movement', 'move', 0, 'move', [{ name: 'points', kind: 'nums' }], 'Walk a list of waypoints in order (x z x z …, up to 16 points).', 'route 10 0 10 30 -20 30'),
  C('patrol', 'Movement', 'move', 0, 'move', [{ name: 'radius', kind: 'num', min: 10, max: 300 }], 'One full lap around the home point.', 'patrol 60'),
  C('wander', 'Movement', 'move', 0, 'move', [{ name: 'radius', kind: 'num', min: 5, max: 200 }, { name: 'seconds', kind: 'num', min: 1, max: 3600 }], 'Stroll around the home point for a while (greeters, patrols).', 'wander 20 60'),
  C('home', 'Movement', 'move', 0, 'move', [], 'Return to the home point.', 'home'),
  C('come', 'Movement', 'move', 0, 'move', [], 'Walk to you (the owner).', 'come'),
  C('follow', 'Movement', 'move', 0, 'move', [{ name: 'seconds', kind: 'num', opt: true, min: 1, max: 3600 }], 'Follow you for a while (default 60 s).', 'follow 120'),
  C('wait', 'Movement', 'move', 0, 1, [{ name: 'seconds', kind: 'num', min: 0.5, max: 3600 }], 'Hold position.', 'wait 30'),
  // sensing
  C('scan', 'Sensors', 'scan', 0.5, 1, [{ name: 'what', kind: 'word', opt: true, words: ['minerals', 'life', 'all'] }], 'Log what is here: mineral concentrations, or wildlife within scan range.', 'scan life'),
  C('survey', 'Sensors', 'survey', 0.4, 'move', [{ name: 'family', kind: 'word' }, { name: 'radius', kind: 'num', opt: true, min: 20, max: 500 }, { name: 'step', kind: 'num', opt: true, min: 10, max: 200 }], 'Walk a grid, sample a resource family at every point and publish a survey REPORT (best spots, average, a map). Cost is per sample point.', 'survey metal 200 40'),
  C('note', 'Sensors', 'scan', 0.05, 0.2, [{ name: 'text', kind: 'text' }], 'Add a line to the report being written.', 'note "checked the north field"'),
  C('report', 'Sensors', 'scan', 1, 0.5, [{ name: 'title', kind: 'text', opt: true }], 'Publish the notes as a REPORT you can read at the terminal.', 'report "Night watch"'),
  // cargo + storage
  C('collect', 'Cargo & storage', 'extract', 2, 3, [], 'Empty the hopper of your extractor (within 15 m) into the cargo.', 'collect'),
  C('store', 'Cargo & storage', 'storage', 0.3, 2, [{ name: 'what', kind: 'word', opt: true }, { name: 'qty', kind: 'num', opt: true, min: 1, max: 100000 }], 'Put cargo into the storage furniture of your structure (within 25 m): a family, an item key, `items` or `all`.', 'store all'),
  C('take', 'Cargo & storage', 'storage', 0.3, 2, [{ name: 'what', kind: 'word' }, { name: 'qty', kind: 'num', opt: true, min: 1, max: 100000 }], 'Take a family (or an item key) out of your structure storage into the cargo.', 'take metal 50'),
  C('organize', 'Cargo & storage', 'organize', 3, 6, [], 'Tidy your structure storage: merge split stacks, weapons to the racks, food to the coolers, the rest to chests and crates.', 'organize'),
  C('give', 'Cargo & storage', 'cargo', 0.2, 1, [{ name: 'what', kind: 'word', opt: true }, { name: 'qty', kind: 'num', opt: true, min: 1, max: 100000 }], 'Hand cargo to you (within 10 m): a family, an item key or `all`.', 'give all'),
  // crafting
  C('craft', 'Crafting', 'craft', 2, 5, [{ name: 'component', kind: 'word' }, { name: 'count', kind: 'num', opt: true, min: 1, max: 10 }], 'Assemble a COMPONENT from resources in the cargo (near a town or a structure). Costs 2 + 1.5 × complexity each.', 'craft metal_ingot 3'),
  // commerce
  C('price', 'Vending', 'vend', 0.1, 0.2, [{ name: 'what', kind: 'word' }, { name: 'credits', kind: 'num', min: 0, max: 1000000 }], 'Set a sale price on cargo (per unit for stacks). Travelers buy from the droid (K near it). 0 = not for sale.', 'price metal 12'),
  C('sell', 'Vending', 'vend', 2, 2, [{ name: 'what', kind: 'word' }, { name: 'qty', kind: 'num', opt: true, min: 4, max: 100000 }], 'Sell cargo to the nearest town\'s stores (within 400 m of the square) — 1 credit per 4 units.', 'sell ore 200'),
  C('post', 'Vending', 'vend', 3, 2, [{ name: 'what', kind: 'word' }, { name: 'credits', kind: 'num', min: 1, max: 1000000 }, { name: 'qty', kind: 'num', opt: true, min: 1, max: 100000 }], 'Post cargo on the bazaar (near a town) at a price per lot — it sells while the droid works.', 'post metal 300 50'),
  // social
  C('say', 'Social', 'social', 0.1, 0.4, [{ name: 'text', kind: 'text' }], 'Speak a line (a bubble over the droid, and the log).', 'say Hello there'),
  C('log', 'Social', 'social', 0.02, 0.1, [{ name: 'text', kind: 'text' }], 'Write a line to the droid\'s log only.', 'log "trip done"'),
  C('advertise', 'Social', 'social', 0.5, 1, [{ name: 'text', kind: 'text' }], 'Call out to everyone within 40 m (at most every 30 s).', 'advertise "Fresh ore, fair prices!"'),
  C('greet', 'Social', 'social', 0.1, 1, [{ name: 'who', kind: 'word', opt: true, words: ['player', 'versona', 'any'] }], 'Greet the nearest traveler or townsperson within 10 m by name.', 'greet any'),
  // combat
  C('attack', 'Combat', 'attack', 1.5, 1.2, [{ name: 'target', kind: 'any', opt: true, words: ['nearest', 'weakest', 'strongest'] }], 'Strike a creature in range: `nearest`, `weakest`, `strongest` or a creature record. Never players or townsfolk.', 'attack weakest'),
  C('guard', 'Combat', 'attack', 1.5, 'move', [{ name: 'radius', kind: 'num', min: 5, max: 300 }, { name: 'seconds', kind: 'num', min: 1, max: 3600 }], 'Hold this spot for a while and attack any creature that comes within the radius.', 'guard 30 600'),
  // medical
  C('heal', 'Medical', 'heal', 1, 1.5, [{ name: 'who', kind: 'word', opt: true, words: ['owner', 'player', 'versona', 'droid', 'any'] }], 'Heal the nearest injured within 8 m: you, a traveler, a townsperson or a droid.', 'heal any'),
  // structures
  C('repair', 'Structures', 'repair', 3, 4, [], 'Repair your nearest structure (within 30 m): +60 condition.', 'repair'),
  C('upkeep', 'Structures', 'repair', 0.5, 1, [{ name: 'credits', kind: 'num', min: 1, max: UPKEEP_CALL_MAX }], `Pay maintenance into your nearest structure's pool from YOUR wallet (≤ ${UPKEEP_CALL_MAX} a call, ≤ ${UPKEEP_HOUR_CAP} an hour per droid).`, 'upkeep 100'),
  // ship
  C('ship_status', 'Ship', 'ship', 0.5, 1, [], 'Report your active ship: hull, fittings, hold.', 'ship_status'),
  C('ship_load', 'Ship', 'ship', 2, 3, [{ name: 'what', kind: 'word', opt: true }, { name: 'qty', kind: 'num', opt: true, min: 1, max: 100000 }], 'Load cargo into the active ship\'s hold (at a city spaceport).', 'ship_load all'),
  C('ship_unload', 'Ship', 'ship', 2, 3, [{ name: 'what', kind: 'word', opt: true }], 'Unload the active ship\'s hold into the cargo (at a city spaceport).', 'ship_unload'),
  C('ship_fit', 'Ship', 'ship', 4, 5, [{ name: 'component', kind: 'word', opt: true }], 'Fit ship components from the cargo into the active ship (the old part comes back to the cargo).', 'ship_fit comp_engine'),
  C('ship_select', 'Ship', 'ship', 0.5, 0.5, [{ name: 'n', kind: 'num', min: 1, max: 8 }], 'Make your Nth ship the active one.', 'ship_select 2'),
]) COMMANDS[c.name] = c;

export interface QuerySpec {
  name: string;
  group: string;
  params: ParamSpec[];
  cap: DroidCap | null;
  returns: string;
  doc: string;
}
const Q = (name: string, group: string, cap: DroidCap | null, params: ParamSpec[], returns: string, doc: string): QuerySpec =>
  ({ name, group, cap, params, returns, doc });
const R: ParamSpec = { name: 'radius', kind: 'num', opt: true, min: 1, max: 1200 };

export const QUERIES: Record<string, QuerySpec> = {};
for (const q of [
  Q('pos_x', 'Self', null, [], 'number', 'Where the droid stands (x).'),
  Q('pos_z', 'Self', null, [], 'number', 'Where the droid stands (z).'),
  Q('home_x', 'Self', null, [], 'number', 'The home point (x).'),
  Q('home_z', 'Self', null, [], 'number', 'The home point (z).'),
  Q('hp', 'Self', null, [], 'number', 'Current health.'),
  Q('max_hp', 'Self', null, [], 'number', 'Full health.'),
  Q('maint', 'Self', null, [], 'number', 'Maintenance credits left in the pool.'),
  Q('clock', 'Self', null, [], 'number', 'Seconds this program has run.'),
  Q('cycles', 'Self', null, [], 'number', 'How many times the program has looped.'),
  Q('owner_dist', 'Self', null, [], 'number', 'Distance to you (-1 when you are elsewhere).'),
  Q('owner', 'Self', null, [], 'record', 'You: {x, z, dist, hp} or nothing.'),
  Q('cargo', 'Cargo', null, [{ name: 'what', kind: 'word', opt: true }], 'number', 'Units in the cargo (all, or one family / item key).'),
  Q('cargo_free', 'Cargo', null, [], 'number', 'Free cargo units.'),
  Q('slots_free', 'Cargo', null, [], 'number', 'Free cargo slots.'),
  Q('creature', 'World', 'scan', [R], 'record', 'The nearest live creature: {id, species, x, z, dist, hp} or nothing.'),
  Q('creatures', 'World', 'scan', [R], 'number', 'How many creatures within the radius.'),
  Q('player', 'World', 'scan', [R], 'record', 'The nearest other traveler: {name, x, z, dist, hp} or nothing.'),
  Q('players', 'World', 'scan', [R], 'number', 'How many travelers within the radius.'),
  Q('versona', 'World', 'scan', [R], 'record', 'The nearest townsperson: {id, name, x, z, dist, hp} or nothing.'),
  Q('versonas', 'World', 'scan', [R], 'number', 'How many townsfolk within the radius.'),
  Q('injured', 'World', 'scan', [R], 'record', 'The nearest injured person or droid: {kind, name, x, z, dist, hp, max} or nothing.'),
  Q('conc', 'World', 'scan', [{ name: 'family', kind: 'word' }], 'number', 'Concentration 0..1 of the best live spawn of a family where the droid stands.'),
  Q('town', 'World', null, [], 'record', 'The nearest settlement: {name, x, z, dist}.'),
  Q('extractor', 'Property', null, [R], 'record', 'Your nearest extractor: {id, x, z, dist, stored, type} or nothing.'),
  Q('structure', 'Property', null, [R], 'record', 'Your nearest structure: {id, x, z, dist, type, condition, maint} or nothing.'),
  Q('stored', 'Property', null, [{ name: 'what', kind: 'word', opt: true }], 'number', 'Units in your nearest structure\'s storage (within 25 m).'),
  Q('ship', 'Property', null, [], 'record', 'Your active ship: {name, chassis, fitted, hold_used, hold_free} or nothing.'),
  Q('sales', 'Property', null, [], 'number', 'Credits this droid has earned vending.'),
  Q('ok', 'Program', null, [], 'boolean', 'Did the last command succeed?'),
  Q('result', 'Program', null, [], 'string', 'What the last command said.'),
  Q('last', 'Program', null, [], 'any', 'The value the last command returned (a count, a report id …).'),
]) QUERIES[q.name] = q;

export interface EventSpec { name: string; arg: string | null; def: number; doc: string }
export const EVENTS: Record<string, EventSpec> = {};
for (const e of [
  { name: 'creature_near', arg: 'radius', def: 25, doc: 'A live creature is within the radius.' },
  { name: 'player_near', arg: 'radius', def: 10, doc: 'Another traveler is within the radius.' },
  { name: 'versona_near', arg: 'radius', def: 10, doc: 'A townsperson is within the radius.' },
  { name: 'owner_near', arg: 'radius', def: 10, doc: 'You are within the radius.' },
  { name: 'owner_far', arg: 'radius', def: 60, doc: 'You are farther than the radius (or elsewhere).' },
  { name: 'injured', arg: 'radius', def: 20, doc: 'Someone injured is within the radius.' },
  { name: 'hurt', arg: 'percent', def: 50, doc: 'The droid\'s health is below the percent.' },
  { name: 'low_maint', arg: 'credits', def: 20, doc: 'The maintenance pool is below the credits.' },
  { name: 'cargo_full', arg: null, def: 0, doc: 'The cargo is full.' },
  { name: 'cargo_empty', arg: null, def: 0, doc: 'The cargo is empty.' },
  { name: 'sale', arg: null, def: 0, doc: 'Someone just bought from the droid.' },
  { name: 'attacked', arg: null, def: 0, doc: 'The droid just took damage.' },
] as EventSpec[]) EVENTS[e.name] = e;

export const BUILTINS: Record<string, string> = {
  abs: 'abs(x)', min: 'min(a, b, …)', max: 'max(a, b, …)', floor: 'floor(x)', ceil: 'ceil(x)',
  round: 'round(x)', sqrt: 'sqrt(x)', clamp: 'clamp(x, lo, hi)', len: 'len(list or text)',
  str: 'str(x)', num: 'num(text)', dist: 'dist(x1, z1, x2, z2) or dist(a, b)', range: 'range(n) → [0 … n-1]',
  contains: 'contains(list or text, x)', push: 'push(list, x) → a new list', join: 'join(list, sep)',
  upper: 'upper(text)', lower: 'lower(text)', rand: 'rand(n) → 0 … n-1',
};

const KEYWORDS = new Set([
  'let', 'if', 'elif', 'else', 'end', 'while', 'repeat', 'forever', 'for', 'in', 'def', 'on', 'every',
  'return', 'break', 'continue', 'stop', 'loop', 'call', 'and', 'or', 'not', 'true', 'false', 'null', 'nothing',
]);

// ─── Lexer ─────────────────────────────────────────────────────────────────────────────────────

interface Tok { t: 'num' | 'str' | 'id' | 'op'; v: string; n?: number; s: number; e: number }

const OPS2 = ['==', '!=', '<=', '>=', '+=', '-=', '&&', '||'];
const OPS1 = '+-*/%<>=()[],.!';

function lex(src: string, line: number): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  const bad = (why: string): never => { throw new DroidError(`Line ${line}: ${why}`); };
  while (i < src.length) {
    const ch = src[i];
    if (ch === ' ' || ch === '\t' || ch === '\r') { i++; continue; }
    if (ch === '#') break;
    if (ch === '"' || ch === "'") {
      const q = ch; let j = i + 1; let v = '';
      while (j < src.length && src[j] !== q) {
        if (src[j] === '\\' && j + 1 < src.length) { v += src[j + 1] === 'n' ? '\n' : src[j + 1]; j += 2; continue; }
        v += src[j]; j++;
      }
      if (j >= src.length) bad('a text is missing its closing quote');
      out.push({ t: 'str', v: v.slice(0, LIMITS.str), s: i, e: j + 1 });
      i = j + 1; continue;
    }
    if ((ch >= '0' && ch <= '9') || (ch === '.' && src[i + 1] >= '0' && src[i + 1] <= '9')) {
      let j = i; while (j < src.length && /[0-9._]/.test(src[j])) j++;
      const text = src.slice(i, j).replace(/_/g, '');
      const n = Number(text);
      if (!Number.isFinite(n)) bad(`'${text}' is not a number`);
      out.push({ t: 'num', v: text, n, s: i, e: j });
      i = j; continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i; while (j < src.length && /[A-Za-z0-9_]/.test(src[j])) j++;
      out.push({ t: 'id', v: src.slice(i, j).toLowerCase(), s: i, e: j });
      i = j; continue;
    }
    const two = src.slice(i, i + 2);
    if (OPS2.includes(two)) { out.push({ t: 'op', v: two, s: i, e: i + 2 }); i += 2; continue; }
    if (OPS1.includes(ch)) { out.push({ t: 'op', v: ch, s: i, e: i + 1 }); i++; continue; }
    bad(`unexpected '${ch}'`);
  }
  return out;
}

/** The line with a trailing # comment removed (quotes respected). */
function stripComment(s: string): string {
  let q = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) { if (ch === '\\') { i++; continue; } if (ch === q) q = ''; continue; }
    if (ch === '"' || ch === "'") { q = ch; continue; }
    if (ch === '#') return s.slice(0, i);
  }
  return s;
}

// ─── AST ───────────────────────────────────────────────────────────────────────────────────────

export type Expr =
  | { k: 'num'; v: number }
  | { k: 'str'; v: string }
  | { k: 'lit'; v: boolean | null }
  | { k: 'var'; name: string; raw?: string }
  | { k: 'list'; items: Expr[] }
  | { k: 'bin'; op: string; a: Expr; b: Expr }
  | { k: 'un'; op: '-' | 'not'; a: Expr }
  | { k: 'call'; name: string; args: Expr[] }
  | { k: 'field'; obj: Expr; name: string }
  | { k: 'index'; obj: Expr; idx: Expr };

export type Stmt =
  | { k: 'let'; name: string; e: Expr; line: number }
  | { k: 'assign'; name: string; op: string; e: Expr; line: number }
  | { k: 'if'; arms: Array<{ cond: Expr; body: Stmt[] }>; els: Stmt[] | null; line: number }
  | { k: 'while'; cond: Expr; body: Stmt[]; line: number }
  | { k: 'repeat'; n: Expr; body: Stmt[]; line: number }
  | { k: 'forever'; body: Stmt[]; line: number }
  | { k: 'for'; name: string; list: Expr; body: Stmt[]; line: number }
  | { k: 'def'; name: string; params: string[]; body: Stmt[]; line: number }
  | { k: 'on'; event: string; args: number[]; body: Stmt[]; line: number }
  | { k: 'every'; secs: number; body: Stmt[]; line: number }
  | { k: 'return'; e: Expr | null; line: number }
  | { k: 'break'; line: number }
  | { k: 'continue'; line: number }
  | { k: 'stop'; line: number }
  | { k: 'loop'; line: number }
  | { k: 'cmd'; name: string; args: Expr[]; line: number }
  | { k: 'expr'; e: Expr; line: number };

// ─── Expression parser (Pratt) ─────────────────────────────────────────────────────────────────

const BIN_PREC: Record<string, number> = {
  or: 1, '||': 1, and: 2, '&&': 2,
  '==': 4, '!=': 4, '<': 4, '<=': 4, '>': 4, '>=': 4,
  '+': 5, '-': 5, '*': 6, '/': 6, '%': 6,
};

class ExprParser {
  private i = 0;
  constructor(private toks: Tok[], private line: number) {}
  private bad(why: string): never { throw new DroidError(`Line ${this.line}: ${why}`); }
  private peek(): Tok | undefined { return this.toks[this.i]; }
  private next(): Tok { const t = this.toks[this.i++]; if (!t) this.bad('the expression ends too early'); return t; }
  private isOp(v: string): boolean { const t = this.peek(); return !!t && t.t === 'op' && t.v === v; }
  done(): boolean { return this.i >= this.toks.length; }
  rest(): Tok[] { return this.toks.slice(this.i); }

  parse(minPrec = 0): Expr {
    let left = this.unary();
    for (;;) {
      const t = this.peek();
      if (!t) break;
      const op = t.t === 'op' ? t.v : t.t === 'id' && (t.v === 'and' || t.v === 'or') ? t.v : '';
      const p = BIN_PREC[op];
      if (p === undefined || p < minPrec || p === 0) break;
      this.i++;
      const right = this.parse(p + 1);
      const norm = op === '&&' ? 'and' : op === '||' ? 'or' : op;
      left = { k: 'bin', op: norm, a: left, b: right };
    }
    return left;
  }

  private unary(): Expr {
    const t = this.peek();
    if (t && t.t === 'op' && t.v === '-') { this.i++; return { k: 'un', op: '-', a: this.unary() }; }
    if (t && ((t.t === 'op' && t.v === '!') || (t.t === 'id' && t.v === 'not'))) { this.i++; return { k: 'un', op: 'not', a: this.parse(3) }; }
    return this.postfix(this.atom());
  }

  private postfix(e: Expr): Expr {
    for (;;) {
      if (this.isOp('.')) {
        this.i++;
        const f = this.next();
        if (f.t !== 'id') this.bad('a field name must follow the dot');
        e = { k: 'field', obj: e, name: f.v };
      } else if (this.isOp('[')) {
        this.i++;
        const idx = this.parse();
        if (!this.isOp(']')) this.bad("missing ']'");
        this.i++;
        e = { k: 'index', obj: e, idx };
      } else return e;
    }
  }

  private atom(): Expr {
    const t = this.next();
    if (t.t === 'num') return { k: 'num', v: t.n ?? 0 };
    if (t.t === 'str') return { k: 'str', v: t.v };
    if (t.t === 'op' && t.v === '(') {
      const e = this.parse();
      if (!this.isOp(')')) this.bad("missing ')'");
      this.i++;
      return e;
    }
    if (t.t === 'op' && t.v === '[') {
      const items: Expr[] = [];
      if (!this.isOp(']')) {
        for (;;) {
          items.push(this.parse());
          if (this.isOp(',')) { this.i++; continue; }
          break;
        }
      }
      if (!this.isOp(']')) this.bad("missing ']'");
      this.i++;
      if (items.length > LIMITS.list) this.bad(`lists hold at most ${LIMITS.list} items`);
      return { k: 'list', items };
    }
    if (t.t === 'id') {
      if (t.v === 'true' || t.v === 'false') return { k: 'lit', v: t.v === 'true' };
      if (t.v === 'null' || t.v === 'nothing') return { k: 'lit', v: null };
      if (KEYWORDS.has(t.v) && t.v !== 'not') this.bad(`'${t.v}' cannot be used in an expression`);
      if (this.isOp('(')) {
        this.i++;
        const args: Expr[] = [];
        if (this.done()) this.bad(`missing ')' after ${t.v}(`);
        if (!this.isOp(')')) {
          for (;;) {
            if (this.done()) this.bad(`missing ')' after the arguments of ${t.v}`);
            args.push(this.parse());
            if (this.isOp(',')) { this.i++; continue; }
            break;
          }
        }
        if (!this.isOp(')')) this.bad(`missing ')' after the arguments of ${t.v}`);
        this.i++;
        return { k: 'call', name: t.v, args };
      }
      return { k: 'var', name: t.v };
    }
    return this.bad(`unexpected '${t.v}'`);
  }
}

function parseExprToks(toks: Tok[], line: number): Expr {
  if (toks.length === 0) throw new DroidError(`Line ${line}: an expression is missing`);
  const p = new ExprParser(toks, line);
  const e = p.parse();
  if (!p.done()) throw new DroidError(`Line ${line}: unexpected '${p.rest()[0].v}' — use commas between arguments that are expressions`);
  return e;
}

function parseExprText(text: string, line: number): Expr {
  return parseExprToks(lex(text, line), line);
}

// ─── Argument splitting (the easy syntax) ──────────────────────────────────────────────────────

/** Split command arguments: by top-level commas if any, else by whitespace with operators glued. */
export function splitArgs(rest: string): string[] {
  const s = rest.trim();
  if (!s) return [];
  const pieces: string[] = [];
  let depth = 0, q = '', cur = '';
  let hasComma = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) { cur += ch; if (ch === '\\' && i + 1 < s.length) { cur += s[++i]; continue; } if (ch === q) q = ''; continue; }
    if (ch === '"' || ch === "'") { q = ch; cur += ch; continue; }
    if (ch === '(' || ch === '[') depth++;
    if (ch === ')' || ch === ']') depth--;
    if (ch === ',' && depth === 0) { hasComma = true; pieces.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  pieces.push(cur.trim());
  if (hasComma) return pieces;
  // whitespace split at depth 0
  const atoms: string[] = [];
  depth = 0; q = ''; cur = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) { cur += ch; if (ch === '\\' && i + 1 < s.length) { cur += s[++i]; continue; } if (ch === q) q = ''; continue; }
    if (ch === '"' || ch === "'") { q = ch; cur += ch; continue; }
    if (ch === '(' || ch === '[') depth++;
    if (ch === ')' || ch === ']') depth--;
    if ((ch === ' ' || ch === '\t') && depth === 0) { if (cur) atoms.push(cur); cur = ''; continue; }
    cur += ch;
  }
  if (cur) atoms.push(cur);
  // Glue operators: an atom that IS an operator joins both neighbours; an atom ending in an
  // operator joins the next; one starting with a binary operator (not '-') joins the previous.
  const isOpAtom = (a: string): boolean => /^(==|!=|<=|>=|[+\-*/%<>]|and|or|&&|\|\|)$/i.test(a);
  const out: string[] = [];
  for (let i = 0; i < atoms.length; i++) {
    let a = atoms[i];
    if (isOpAtom(a) && out.length > 0 && i + 1 < atoms.length) { out[out.length - 1] += ` ${a} ${atoms[++i]}`; continue; }
    if (/^not$/i.test(a) && i + 1 < atoms.length) { a = `not ${atoms[++i]}`; }
    if (out.length > 0 && /[+\-*/%<>=]$/.test(out[out.length - 1]) && !/^["']/.test(out[out.length - 1].slice(-1))) { out[out.length - 1] += ` ${a}`; continue; }
    if (out.length > 0 && /^(==|!=|<=|>=|[+*/%<>])/.test(a)) { out[out.length - 1] += ` ${a}`; continue; }
    out.push(a);
  }
  return out;
}

/**
 * THE WORD RULE: a bare word where a command (or a sensor) expects a WORD is taken literally —
 * `store metal`, `move n 40`, `cargo(ore)` — unless it is the name of one of the program's own
 * variables, which passes that variable's value. A word from the param's fixed list (n/s/e/w,
 * nearest/weakest …) is always literal.
 */
function resolveWords(ast: Stmt[]): void {
  const known = new Set<string>();
  const collect = (b: Stmt[]): void => {
    for (const s of b) {
      if (s.k === 'let' || s.k === 'assign') known.add(s.name);
      if (s.k === 'for') known.add(s.name);
      if (s.k === 'def') for (const p of s.params) known.add(p);
      for (const sub of subBlocks(s)) collect(sub);
    }
  };
  collect(ast);
  const wordOf = (e: Expr, p: ParamSpec | undefined): Expr => {
    if (!p || e.k !== 'var') return e;
    if (p.kind === 'text') return known.has(e.name) ? e : { k: 'str', v: e.raw ?? e.name };
    const listed = !!p.words && p.words.includes(e.name);
    if (p.kind === 'word' && (listed || !known.has(e.name))) return { k: 'str', v: e.name };
    if (p.kind === 'any' && listed) return { k: 'str', v: e.name };
    return e;
  };
  const fixExpr = (e: Expr): Expr => {
    switch (e.k) {
      case 'call': {
        const q = QUERIES[e.name];
        e.args = e.args.map((a, i) => fixExpr(q ? wordOf(a, q.params[i]) : a));
        return e;
      }
      case 'list': e.items = e.items.map(fixExpr); return e;
      case 'bin': e.a = fixExpr(e.a); e.b = fixExpr(e.b); return e;
      case 'un': e.a = fixExpr(e.a); return e;
      case 'field': e.obj = fixExpr(e.obj); return e;
      case 'index': e.obj = fixExpr(e.obj); e.idx = fixExpr(e.idx); return e;
      default: return e;
    }
  };
  const walk = (b: Stmt[]): void => {
    for (const s of b) {
      if (s.k === 'cmd') {
        const spec = COMMANDS[s.name];
        s.args = s.args.map((a, i) => fixExpr(spec ? wordOf(a, spec.params[Math.min(i, spec.params.length - 1)]) : a));
      } else if (s.k === 'let' || s.k === 'assign') s.e = fixExpr(s.e);
      else if (s.k === 'expr') s.e = fixExpr(s.e);
      else if (s.k === 'return' && s.e) s.e = fixExpr(s.e);
      else if (s.k === 'if') for (const arm of s.arms) arm.cond = fixExpr(arm.cond);
      else if (s.k === 'while') s.cond = fixExpr(s.cond);
      else if (s.k === 'repeat') s.n = fixExpr(s.n);
      else if (s.k === 'for') s.list = fixExpr(s.list);
      for (const sub of subBlocks(s)) walk(sub);
    }
  };
  walk(ast);
}

function subBlocks(s: Stmt): Stmt[][] {
  switch (s.k) {
    case 'if': return [...s.arms.map((a) => a.body), ...(s.els ? [s.els] : [])];
    case 'while': case 'repeat': case 'forever': case 'for': case 'def': case 'on': case 'every': return [s.body];
    default: return [];
  }
}

// ─── Statement parser (lines → blocks) ─────────────────────────────────────────────────────────

interface OpenBlock {
  node: Stmt;
  body: Stmt[];
  kind: string;
  line: number;
}

/** A text argument: an expression when the whole text parses as one (a lone word stays prose unless
 *  the program has a variable of that name — the word rule), else the words as written. */
function textArg(r: string, line: number): Expr[] {
  if (!r) return [];
  try {
    const e = parseExprText(r, line);
    if (e.k === 'var') return [{ k: 'var', name: e.name, raw: r.slice(0, LIMITS.str) }];
    return [e];
  } catch {
    if (r.startsWith('"') || r.startsWith("'")) return [parseExprText(r, line)]; // a real mistake inside quotes: report it
    return [{ k: 'str', v: r.slice(0, LIMITS.str) }];
  }
}

export function parseProgram(source: string): Stmt[] {
  if (source.length > LIMITS.source) throw new DroidError(`Programs cap at ${LIMITS.source} characters`);
  const lines = source.split('\n');
  if (lines.length > LIMITS.lines) throw new DroidError(`Programs cap at ${LIMITS.lines} lines`);
  const root: Stmt[] = [];
  const stack: OpenBlock[] = [];
  const cur = (): Stmt[] => (stack.length ? stack[stack.length - 1].body : root);
  for (let li = 0; li < lines.length; li++) {
    const line = li + 1;
    const raw = lines[li];
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed.startsWith(';') || trimmed.startsWith('#') || trimmed.startsWith('//')) continue;
    const text = stripComment(raw).trim().replace(/:$/, '').trim();
    if (!text) continue;
    const toks = lex(text, line);
    if (toks.length === 0) continue;
    const bad = (why: string): never => { throw new DroidError(`Line ${line}: ${why}`); };
    const t0 = toks[0];
    const kw = t0.t === 'id' ? t0.v : '';
    const restToks = toks.slice(1);
    const restText = text.slice(t0.e);
    const open = (node: Stmt, body: Stmt[], kind: string): void => { cur().push(node); stack.push({ node, body, kind, line }); };
    const topOnly = (what: string): void => { if (stack.length) bad(`'${what}' belongs at the top level, not inside another block`); };

    if (kw === 'end') {
      if (restToks.length) bad("'end' stands alone");
      if (!stack.length) bad("'end' without a block to close");
      stack.pop();
      continue;
    }
    if (kw === 'elif' || kw === 'else') {
      const top = stack[stack.length - 1];
      if (!top || top.kind !== 'if') bad(`'${kw}' without an 'if'`);
      const ifn = top.node as Extract<Stmt, { k: 'if' }>;
      if (ifn.els) bad(`'${kw}' after 'else'`);
      if (kw === 'else') {
        if (restToks.length) bad("'else' stands alone");
        ifn.els = [];
        top.body = ifn.els;
      } else {
        const cond = parseExprToks(restToks, line);
        const arm = { cond, body: [] as Stmt[] };
        ifn.arms.push(arm);
        top.body = arm.body;
      }
      continue;
    }
    if (kw === 'if') {
      const arm = { cond: parseExprToks(restToks, line), body: [] as Stmt[] };
      const node: Stmt = { k: 'if', arms: [arm], els: null, line };
      open(node, arm.body, 'if');
      continue;
    }
    if (kw === 'while') {
      const node: Stmt = { k: 'while', cond: parseExprToks(restToks, line), body: [], line };
      open(node, node.body, 'while');
      continue;
    }
    if (kw === 'repeat') {
      const node: Stmt = { k: 'repeat', n: parseExprToks(restToks, line), body: [], line };
      open(node, node.body, 'repeat');
      continue;
    }
    if (kw === 'forever') {
      if (restToks.length) bad("'forever' stands alone");
      const node: Stmt = { k: 'forever', body: [], line };
      open(node, node.body, 'forever');
      continue;
    }
    if (kw === 'for') {
      const nm = restToks[0];
      if (!nm || nm.t !== 'id' || KEYWORDS.has(nm.v)) bad("write 'for <name> in <list>'");
      if (!restToks[1] || restToks[1].t !== 'id' || restToks[1].v !== 'in') bad("write 'for <name> in <list>'");
      const node: Stmt = { k: 'for', name: nm.v, list: parseExprToks(restToks.slice(2), line), body: [], line };
      open(node, node.body, 'for');
      continue;
    }
    if (kw === 'def') {
      topOnly('def');
      const nm = restToks[0];
      if (!nm || nm.t !== 'id' || KEYWORDS.has(nm.v)) bad("write 'def <name>' or 'def <name>(a, b)'");
      if (COMMANDS[nm.v] || QUERIES[nm.v] || BUILTINS[nm.v]) bad(`'${nm.v}' is already a built-in name — choose another`);
      const params: string[] = [];
      const pt = restToks.slice(1).filter((x) => !(x.t === 'op' && (x.v === '(' || x.v === ')' || x.v === ',')));
      for (const p of pt) {
        if (p.t !== 'id' || KEYWORDS.has(p.v)) bad(`'${p.v}' cannot be a parameter name`);
        params.push(p.v);
      }
      const node: Stmt = { k: 'def', name: nm.v, params, body: [], line };
      open(node, node.body, 'def');
      continue;
    }
    if (kw === 'on') {
      topOnly('on');
      const ev = restToks[0];
      if (!ev || ev.t !== 'id' || !EVENTS[ev.v]) bad(`unknown event '${ev?.v ?? ''}' (${Object.keys(EVENTS).join(', ')})`);
      const spec = EVENTS[ev.v];
      const args: number[] = [];
      for (const a of restToks.slice(1)) {
        if (a.t === 'num') args.push(a.n ?? 0);
        else if (!(a.t === 'op' && a.v === ',')) bad(`an event takes a plain number (${spec.arg ?? 'nothing'})`);
      }
      if (!spec.arg && args.length) bad(`'${ev.v}' takes no number`);
      if (args.length > 1) bad(`'${ev.v}' takes one number`);
      if (spec.arg && args.length === 0) args.push(spec.def);
      if (args.length && (args[0] < 0 || args[0] > 100000)) bad('that number is out of range');
      const node: Stmt = { k: 'on', event: ev.v, args, body: [], line };
      open(node, node.body, 'on');
      continue;
    }
    if (kw === 'every') {
      topOnly('every');
      const n = restToks[0];
      if (!n || n.t !== 'num' || restToks.length !== 1) bad("write 'every <seconds>'");
      const secs = n.n ?? 0;
      if (secs < 5 || secs > 86400) bad('every runs from 5 seconds to a day (86400)');
      const node: Stmt = { k: 'every', secs, body: [], line };
      open(node, node.body, 'every');
      continue;
    }
    if (kw === 'let') {
      const nm = restToks[0];
      if (!nm || nm.t !== 'id' || KEYWORDS.has(nm.v)) bad("write 'let <name> = <value>'");
      if (!restToks[1] || restToks[1].t !== 'op' || restToks[1].v !== '=') bad("write 'let <name> = <value>'");
      cur().push({ k: 'let', name: nm.v, e: parseExprToks(restToks.slice(2), line), line });
      continue;
    }
    if (kw === 'return') { cur().push({ k: 'return', e: restToks.length ? parseExprToks(restToks, line) : null, line }); continue; }
    if (kw === 'break' || kw === 'continue' || kw === 'stop' || kw === 'loop') {
      if (restToks.length) bad(`'${kw}' stands alone`);
      cur().push({ k: kw, line } as Stmt);
      continue;
    }
    if (kw === 'call') {
      const nm = restToks[0];
      if (!nm || nm.t !== 'id') bad("write 'call <function>'");
      const args = splitArgs(text.slice(nm.e)).map((a) => parseExprText(a, line));
      cur().push({ k: 'expr', e: { k: 'call', name: nm.v, args }, line });
      continue;
    }
    if (t0.t === 'id' && !KEYWORDS.has(kw)) {
      // assignment?
      const t1 = toks[1];
      if (t1 && t1.t === 'op' && (t1.v === '=' || t1.v === '+=' || t1.v === '-=')) {
        cur().push({ k: 'assign', name: kw, op: t1.v, e: parseExprToks(toks.slice(2), line), line });
        continue;
      }
      // a whole-line call expression: name(...) with nothing after
      if (t1 && t1.t === 'op' && t1.v === '(' && !COMMANDS[kw]) {
        cur().push({ k: 'expr', e: parseExprToks(toks, line), line });
        continue;
      }
      // a command (or a user function called command-style)
      const spec = COMMANDS[kw];
      let args: Expr[];
      if (spec && spec.params.length >= 1 && spec.params[0].kind === 'text') {
        args = textArg(restText.trim(), line);
      } else {
        args = splitArgs(restText).map((piece) => parseExprText(piece, line));
      }
      cur().push({ k: 'cmd', name: kw, args, line });
      continue;
    }
    bad(`'${t0.v}' cannot start a line`);
  }
  if (stack.length) {
    const top = stack[stack.length - 1];
    throw new DroidError(`Line ${top.line}: '${top.kind}' has no matching 'end'`);
  }
  return root;
}

// ─── Bytecode ──────────────────────────────────────────────────────────────────────────────────

/** One instruction: [op, a?, b?] — JSON-friendly. */
export type Instr = [string, (string | number | boolean | null)?, (string | number)?];

export interface Handler { kind: 'on' | 'every'; event: string; arg: number; addr: number; line: number }

export interface Program {
  v: 2;
  cls: DroidClass;
  code: Instr[];
  lines: number[];
  funcs: Record<string, { addr: number; params: string[] }>;
  handlers: Handler[];
  /** True when the main program repeats (loop/forever). */
  loops: boolean;
}

export interface CompileResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
  program: Program | null;
  ast: Stmt[] | null;
}

export interface CompileOpts {
  cls: DroidClass;
  /** Home point: literal goto targets are checked against the class leash. */
  homeX?: number;
  homeZ?: number;
}

class Compiler {
  code: Instr[] = [];
  lines: number[] = [];
  funcs: Record<string, { addr: number; params: string[] }> = {};
  handlers: Handler[] = [];
  warnings: string[] = [];
  loops = false;
  private defs = new Map<string, { params: string[] }>();
  private loopStack: Array<{ breaks: number[]; conts: number[]; contTarget: number }> = [];
  private hidden = 0;
  private inDef: string | null = null;
  private inHandler = false;
  private patches: Array<{ at: number; name: string; line: number }> = [];

  constructor(private opts: CompileOpts) {}

  private bad(line: number, why: string): never { throw new DroidError(`Line ${line}: ${why}`); }

  private emit(ins: Instr, line: number): number {
    if (this.code.length >= LIMITS.instr) throw new DroidError(`The program is too large (over ${LIMITS.instr} instructions)`);
    this.code.push(ins);
    this.lines.push(line);
    return this.code.length - 1;
  }

  compile(ast: Stmt[]): void {
    for (const s of ast) if (s.k === 'def') {
      if (this.defs.has(s.name)) this.bad(s.line, `'${s.name}' is defined twice`);
      this.defs.set(s.name, { params: s.params });
    }
    if (this.defs.size > LIMITS.funcs) throw new DroidError(`At most ${LIMITS.funcs} functions`);
    const main = ast.filter((s) => s.k !== 'def' && s.k !== 'on' && s.k !== 'every');
    this.block(main);
    this.emit(['halt'], main.length ? main[main.length - 1].line : 1);
    for (const s of ast) {
      if (s.k === 'def') {
        this.funcs[s.name] = { addr: this.code.length, params: s.params };
        this.inDef = s.name;
        this.block(s.body);
        this.emit(['push', null], s.line);
        this.emit(['ret'], s.line);
        this.inDef = null;
      } else if (s.k === 'on' || s.k === 'every') {
        if (this.handlers.length >= LIMITS.handlers) this.bad(s.line, `at most ${LIMITS.handlers} handlers`);
        const addr = this.code.length;
        this.handlers.push(s.k === 'on'
          ? { kind: 'on', event: s.event, arg: s.args[0] ?? 0, addr, line: s.line }
          : { kind: 'every', event: '', arg: s.secs, addr, line: s.line });
        this.inHandler = true;
        this.block(s.body);
        this.inHandler = false;
        this.emit(['hend'], s.line);
      }
    }
    for (const p of this.patches) {
      const f = this.funcs[p.name];
      if (!f) this.bad(p.line, `unknown function '${p.name}'`);
      (this.code[p.at] as Instr)[1] = f.addr;
    }
  }

  private block(body: Stmt[]): void { for (const s of body) this.stmt(s); }

  private hiddenName(tag: string): string { return `$${tag}${this.hidden++}`; }

  private stmt(s: Stmt): void {
    const L = s.line;
    switch (s.k) {
      case 'let': this.expr(s.e, L); this.emit(['let', s.name], L); return;
      case 'assign':
        if (s.op === '=') this.expr(s.e, L);
        else { this.emit(['get', s.name], L); this.expr(s.e, L); this.emit(['bin', s.op === '+=' ? '+' : '-'], L); }
        this.emit(['set', s.name], L); return;
      case 'if': {
        const ends: number[] = [];
        for (const arm of s.arms) {
          this.expr(arm.cond, L);
          const jf = this.emit(['jf', 0], L);
          this.block(arm.body);
          ends.push(this.emit(['jmp', 0], L));
          (this.code[jf] as Instr)[1] = this.code.length;
        }
        if (s.els) this.block(s.els);
        for (const e of ends) (this.code[e] as Instr)[1] = this.code.length;
        return;
      }
      case 'while': {
        const top = this.code.length;
        this.expr(s.cond, L);
        const jf = this.emit(['jf', 0], L);
        this.loopBody(s.body, top);
        this.emit(['jmp', top], L);
        this.closeLoop(jf);
        return;
      }
      case 'repeat': {
        const c = this.hiddenName('r');
        this.expr(s.n, L);
        this.emit(['let', c], L);
        const top = this.code.length;
        this.emit(['get', c], L); this.emit(['push', 0], L); this.emit(['bin', '>'], L);
        const jf = this.emit(['jf', 0], L);
        const contAt = { v: 0 };
        this.loopStack.push({ breaks: [], conts: [], contTarget: -1 });
        this.block(s.body);
        contAt.v = this.code.length;
        this.emit(['get', c], L); this.emit(['push', 1], L); this.emit(['bin', '-'], L); this.emit(['set', c], L);
        this.emit(['jmp', top], L);
        const lp = this.loopStack.pop()!;
        for (const b of lp.conts) (this.code[b] as Instr)[1] = contAt.v;
        (this.code[jf] as Instr)[1] = this.code.length;
        for (const b of lp.breaks) (this.code[b] as Instr)[1] = this.code.length;
        return;
      }
      case 'forever': {
        if (!this.inDef) this.loops = true;
        const top = this.code.length;
        this.loopStack.push({ breaks: [], conts: [], contTarget: top });
        this.block(s.body);
        this.emit(['tick'], L);
        this.emit(['jmp', top], L);
        const lp = this.loopStack.pop()!;
        for (const b of lp.conts) (this.code[b] as Instr)[1] = top;
        for (const b of lp.breaks) (this.code[b] as Instr)[1] = this.code.length;
        return;
      }
      case 'for': {
        const ls = this.hiddenName('l'), ix = this.hiddenName('i');
        this.expr(s.list, L); this.emit(['let', ls], L);
        this.emit(['push', 0], L); this.emit(['let', ix], L);
        const top = this.code.length;
        this.emit(['get', ix], L); this.emit(['get', ls], L); this.emit(['fn', 'len', 1], L); this.emit(['bin', '<'], L);
        const jf = this.emit(['jf', 0], L);
        this.emit(['get', ls], L); this.emit(['get', ix], L); this.emit(['idx'], L); this.emit(['let', s.name], L);
        this.loopStack.push({ breaks: [], conts: [], contTarget: -1 });
        this.block(s.body);
        const cont = this.code.length;
        this.emit(['get', ix], L); this.emit(['push', 1], L); this.emit(['bin', '+'], L); this.emit(['set', ix], L);
        this.emit(['jmp', top], L);
        const lp = this.loopStack.pop()!;
        for (const b of lp.conts) (this.code[b] as Instr)[1] = cont;
        (this.code[jf] as Instr)[1] = this.code.length;
        for (const b of lp.breaks) (this.code[b] as Instr)[1] = this.code.length;
        return;
      }
      case 'return':
        if (!this.inDef) this.bad(L, "'return' outside a function");
        if (s.e) this.expr(s.e, L); else this.emit(['push', null], L);
        this.emit(['ret'], L); return;
      case 'break': case 'continue': {
        const lp = this.loopStack[this.loopStack.length - 1];
        if (!lp) this.bad(L, `'${s.k}' outside a loop`);
        const at = this.emit(['jmp', 0], L);
        (s.k === 'break' ? lp.breaks : lp.conts).push(at);
        return;
      }
      case 'stop': this.emit(['stop'], L); return;
      case 'loop':
        if (this.inDef) this.bad(L, "'loop' restarts the whole program — use 'forever' or 'while' inside a function");
        if (this.inHandler) this.bad(L, "'loop' cannot restart the program from inside a handler — let the handler end");
        this.loops = true;
        this.emit(['tick'], L);
        this.emit(['loop'], L); return;
      case 'expr':
        this.expr(s.e, L);
        this.emit(['pop'], L); return;
      case 'cmd': {
        if (this.defs.has(s.name)) { this.userCall(s.name, s.args, L); this.emit(['pop'], L); return; }
        const spec = COMMANDS[s.name];
        if (!spec) {
          if (QUERIES[s.name] || BUILTINS[s.name]) this.bad(L, `'${s.name}' is a sensor — use it in an expression, e.g. let x = ${s.name}()`);
          this.bad(L, `unknown command '${s.name}'`);
        }
        this.checkArgs(spec, s.args, L);
        if (!classHas(this.opts.cls, spec.cap)) {
          this.bad(L, `a ${DROID_CLASSES[this.opts.cls].name} cannot ${spec.name} (it needs the ${spec.cap} ability)`);
        }
        this.checkLeash(spec.name, s.args, L);
        for (const a of s.args) this.expr(a, L);
        this.emit(['cmd', s.name, s.args.length], L);
        return;
      }
      case 'def': case 'on': case 'every':
        this.bad(L, `'${s.k}' belongs at the top level`);
    }
  }

  private loopBody(body: Stmt[], top: number): void {
    this.loopStack.push({ breaks: [], conts: [], contTarget: top });
    this.block(body);
  }

  private closeLoop(jf: number): void {
    const lp = this.loopStack.pop()!;
    for (const b of lp.conts) (this.code[b] as Instr)[1] = lp.contTarget;
    (this.code[jf] as Instr)[1] = this.code.length;
    for (const b of lp.breaks) (this.code[b] as Instr)[1] = this.code.length;
  }

  private checkArgs(spec: CmdSpec, args: Expr[], L: number): void {
    const req = spec.params.filter((p) => !p.opt && p.kind !== 'nums').length;
    const variadic = spec.params.some((p) => p.kind === 'nums');
    if (args.length < req) this.bad(L, `${spec.name} needs ${spec.params.map((p) => (p.opt ? `[${p.name}]` : p.name)).join(' ')} — e.g. ${spec.example.split('·')[0].trim()}`);
    if (!variadic && args.length > spec.params.length) this.bad(L, `${spec.name} takes at most ${spec.params.length} argument${spec.params.length === 1 ? '' : 's'}`);
    if (variadic) {
      if (args.length < 2 || args.length % 2 !== 0 || args.length > 32) this.bad(L, `${spec.name} takes pairs of numbers (x z), 1 to 16 points`);
      return;
    }
    args.forEach((a, i) => {
      const p = spec.params[i];
      if (!p) return;
      if (p.kind === 'num' && a.k === 'str') this.bad(L, `${spec.name}: ${p.name} must be a number`);
      if (p.kind === 'num' && a.k === 'num') {
        if (p.min !== undefined && a.v < p.min) this.bad(L, `${spec.name}: ${p.name} must be ${p.min}..${p.max}`);
        if (p.max !== undefined && a.v > p.max) this.bad(L, `${spec.name}: ${p.name} must be ${p.min}..${p.max}`);
      }
      if (p.kind === 'word' && p.words && a.k === 'str' && !p.words.includes(a.v)) {
        this.bad(L, `${spec.name}: '${a.v}' is not one of ${p.words.join(', ')}`);
      }
    });
    if (spec.name === 'attack' && args[0]?.k === 'str' && !['nearest', 'weakest', 'strongest'].includes(args[0].v)) {
      this.bad(L, "attack takes nearest, weakest, strongest or a creature record");
    }
  }

  private checkLeash(name: string, args: Expr[], L: number): void {
    if (this.opts.homeX === undefined || this.opts.homeZ === undefined) return;
    const leash = DROID_CLASSES[this.opts.cls].leashM;
    const far = (x: number, z: number): boolean => Math.hypot(x - (this.opts.homeX ?? 0), z - (this.opts.homeZ ?? 0)) > leash;
    if (name === 'goto' && args.length === 2 && args[0].k === 'num' && args[1].k === 'num' && far(args[0].v, args[1].v)) {
      this.bad(L, `goto target beyond the ${leash} m leash from the home point`);
    }
    if (name === 'route') {
      for (let i = 0; i + 1 < args.length; i += 2) {
        const a = args[i], b = args[i + 1];
        if (a.k === 'num' && b.k === 'num' && far(a.v, b.v)) this.bad(L, `a route point is beyond the ${leash} m leash from the home point`);
      }
    }
  }

  private userCall(name: string, args: Expr[], L: number): void {
    const d = this.defs.get(name)!;
    if (args.length !== d.params.length) this.bad(L, `${name} takes ${d.params.length} argument${d.params.length === 1 ? '' : 's'}`);
    for (const a of args) this.expr(a, L);
    const at = this.emit(['call', 0, args.length], L);
    this.patches.push({ at, name, line: L });
  }

  private expr(e: Expr, L: number): void {
    switch (e.k) {
      case 'num': this.emit(['push', e.v], L); return;
      case 'str': this.emit(['push', e.v], L); return;
      case 'lit': this.emit(['push', e.v], L); return;
      case 'var': this.emit(['get', e.name], L); return;
      case 'list': for (const it of e.items) this.expr(it, L); this.emit(['list', e.items.length], L); return;
      case 'un': this.expr(e.a, L); this.emit([e.op === '-' ? 'neg' : 'not'], L); return;
      case 'bin':
        if (e.op === 'and' || e.op === 'or') {
          this.expr(e.a, L);
          const j = this.emit([e.op, 0], L);
          this.expr(e.b, L);
          (this.code[j] as Instr)[1] = this.code.length;
          return;
        }
        this.expr(e.a, L); this.expr(e.b, L); this.emit(['bin', e.op], L); return;
      case 'field': this.expr(e.obj, L); this.emit(['fld', e.name], L); return;
      case 'index': this.expr(e.obj, L); this.expr(e.idx, L); this.emit(['idx'], L); return;
      case 'call': {
        if (this.defs.has(e.name)) { this.userCall(e.name, e.args, L); return; }
        if (COMMANDS[e.name]) this.bad(L, `'${e.name}' is a command — put it on its own line`);
        const q = QUERIES[e.name];
        if (q) {
          if (q.cap && !classHas(this.opts.cls, q.cap)) this.bad(L, `a ${DROID_CLASSES[this.opts.cls].name} has no ${q.name}() sensor`);
          const req = q.params.filter((p) => !p.opt).length;
          if (e.args.length < req || e.args.length > q.params.length) this.bad(L, `${e.name}() takes ${q.params.length ? q.params.map((p) => (p.opt ? `[${p.name}]` : p.name)).join(', ') : 'no arguments'}`);
          for (const a of e.args) this.expr(a, L);
          this.emit(['fn', e.name, e.args.length], L);
          return;
        }
        if (BUILTINS[e.name]) {
          for (const a of e.args) this.expr(a, L);
          this.emit(['fn', e.name, e.args.length], L);
          return;
        }
        this.bad(L, `unknown function '${e.name}'`);
      }
    }
  }
}

/** Compile DroidScript 2 source for a class. Never throws — errors come back in the result. */
export function compileDroid(source: string, opts: CompileOpts): CompileResult {
  try {
    const ast = parseProgram(source);
    resolveWords(ast);
    const c = new Compiler(opts);
    c.compile(ast);
    const hasCode = c.code.some((ins) => ins[0] !== 'halt');
    if (!hasCode && c.handlers.length === 0) return { ok: false, errors: ['The program is empty'], warnings: [], program: null, ast };
    const warnings = [...c.warnings];
    if (c.loops && !programWaits(ast)) warnings.push('The program loops without waiting or moving — it will burn instructions every second. Add a wait.');
    return {
      ok: true, errors: [], warnings, ast,
      program: { v: 2, cls: opts.cls, code: c.code, lines: c.lines, funcs: c.funcs, handlers: c.handlers, loops: c.loops },
    };
  } catch (e) {
    return { ok: false, errors: [String((e as Error).message ?? e)], warnings: [], program: null, ast: null };
  }
}

function programWaits(ast: Stmt[]): boolean {
  let found = false;
  const walk = (b: Stmt[]): void => {
    for (const s of b) {
      if (s.k === 'cmd') found = true;
      if ('body' in s && Array.isArray((s as { body?: Stmt[] }).body)) walk((s as { body: Stmt[] }).body);
      if (s.k === 'if') { for (const a of s.arms) walk(a.body); if (s.els) walk(s.els); }
    }
  };
  walk(ast);
  return found;
}

// ─── The VM ────────────────────────────────────────────────────────────────────────────────────

/** A pending long action the HOST advances (walking, waiting, crafting …). Opaque to the VM. */
export interface Wait { k: string; [key: string]: unknown }

export interface CmdResult { ok: boolean; msg: string; value?: Val; wait?: Wait | null; /** The pool could not pay — the VM retries this command once funded. */ broke?: boolean }

export interface DroidHost {
  /** A sensor read (QUERIES). */
  query(name: string, args: Val[]): Val;
  /** A command (COMMANDS): does its work now and/or returns a wait. May throw DroidError. */
  command(name: string, args: Val[], line: number): CmdResult;
  /** Advance a wait by dt seconds: the leftover seconds once it is finished, or -1 while it goes on. */
  advance(w: Wait, dt: number): number;
  /** Is this event true right now? */
  event(name: string, arg: number): boolean;
  /** Pay for thinking (credits). False = the pool is empty. */
  charge(credits: number): boolean;
}

interface Frame { ret: number; loc: Record<string, Val> }
interface Saved { pc: number; stack: Val[]; frames: Frame[]; wait: Wait | null }

export interface VmState {
  pc: number;
  stack: Val[];
  frames: Frame[];
  g: Record<string, Val>;
  /** The handler running (-1 = the main program). */
  h: number;
  saved: Saved | null;
  wait: Wait | null;
  /** Per handler: the clock at which it may next fire. */
  next: number[];
  clock: number;
  cycles: number;
  steps: number;
  /** '' running · 'done' finished · 'error' stopped on an error · 'broke' out of credits (resumes when funded). */
  halt: '' | 'done' | 'error' | 'broke';
  err: string;
  ok: boolean;
  msg: string;
  last: Val;
  rng: number;
  line: number;
}

export function freshState(prog: Program, seed: number): VmState {
  return {
    pc: 0, stack: [], frames: [], g: {}, h: -1, saved: null, wait: null,
    next: prog.handlers.map((h) => (h.kind === 'every' ? h.arg : 0)),
    clock: 0, cycles: 0, steps: 0, halt: '', err: '', ok: true, msg: '', last: null,
    rng: (seed >>> 0) || 0x9e3779b9, line: prog.lines[0] ?? 1,
  };
}

const truthy = (v: Val): boolean => !(v === false || v === null || v === 0 || v === '' || (Array.isArray(v) && v.length === 0));

function showVal(v: Val): string {
  if (v === null) return 'nothing';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return `[${v.slice(0, 8).map(showVal).join(', ')}${v.length > 8 ? ', …' : ''}]`;
  const o = v as Record<string, Val>;
  if (typeof o.name === 'string') return o.name;
  if (typeof o.species === 'string') return o.species;
  return `{${Object.keys(o).slice(0, 6).join(', ')}}`;
}

function capStr(s: string): string { return s.length > LIMITS.str ? s.slice(0, LIMITS.str) : s; }

/** A composite value's size as a TREE (sharing counted every time — what JSON will write), stopping past the cap.
 *  Lists are never mutated in place (push / + / [..] build new ones), so a size once measured stays true. */
const cellCache = new WeakMap<object, number>();
export function valueCells(v: Val, cap = LIMITS.cells): number {
  if (typeof v === 'string') return 1 + (v.length >> 6); // a long text weighs like a few items
  if (v === null || typeof v !== 'object') return 1;
  const have = cellCache.get(v);
  if (have !== undefined) return have;
  let n = 1;
  for (const x of (Array.isArray(v) ? v : Object.values(v))) {
    n += valueCells(x, cap);
    if (n > cap) break;
  }
  const out = Math.min(n, cap + 1);
  cellCache.set(v, out);
  return out;
}
/** Everything a program holds (globals, the stack, the frames, a saved main context, the last value) in cells — the
 *  host checks this BEFORE it serialises the state, so a runaway never costs a huge JSON.stringify (2026-09-28). */
export function stateCells(st: VmState): number {
  let n = 0;
  const add = (v: Val): void => { n += valueCells(v, 1 << 20); };
  for (const v of Object.values(st.g ?? {})) add(v);
  for (const v of st.stack ?? []) add(v);
  for (const f of st.frames ?? []) for (const v of Object.values(f.loc ?? {})) add(v);
  if (st.saved) { for (const v of st.saved.stack ?? []) add(v); for (const f of st.saved.frames ?? []) for (const v of Object.values(f.loc ?? {})) add(v); }
  add(st.last ?? null);
  return n;
}

function capCells(v: Val, line: number): Val {
  if (valueCells(v) > LIMITS.cells) throw new DroidError(`Line ${line}: a value may hold at most ${LIMITS.cells} items in all (lists inside lists count every item)`);
  return v;
}

function numOf(v: Val, what: string, line: number): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  throw new DroidError(`Line ${line}: ${what} needs a number, got ${v === null ? 'nothing' : typeof v === 'string' ? `"${v}"` : 'a record'}`);
}

function binop(op: string, a: Val, b: Val, line: number): Val {
  switch (op) {
    case '+':
      if (typeof a === 'string' || typeof b === 'string') return capStr(showVal(a) + showVal(b));
      if (Array.isArray(a) && Array.isArray(b)) { const r = [...a, ...b]; if (r.length > LIMITS.list) throw new DroidError(`Line ${line}: lists hold at most ${LIMITS.list} items`); return capCells(r, line); }
      return numOf(a, '+', line) + numOf(b, '+', line);
    case '-': return numOf(a, '-', line) - numOf(b, '-', line);
    case '*': return numOf(a, '*', line) * numOf(b, '*', line);
    case '/': { const d = numOf(b, '/', line); if (d === 0) throw new DroidError(`Line ${line}: division by zero`); return numOf(a, '/', line) / d; }
    case '%': { const d = numOf(b, '%', line); if (d === 0) throw new DroidError(`Line ${line}: division by zero`); return numOf(a, '%', line) % d; }
    case '==': return JSON.stringify(a) === JSON.stringify(b);
    case '!=': return JSON.stringify(a) !== JSON.stringify(b);
    case '<': case '<=': case '>': case '>=': {
      if (a === null || b === null) return false;
      if (typeof a === 'string' && typeof b === 'string') {
        return op === '<' ? a < b : op === '<=' ? a <= b : op === '>' ? a > b : a >= b;
      }
      const x = numOf(a, op, line), y = numOf(b, op, line);
      return op === '<' ? x < y : op === '<=' ? x <= y : op === '>' ? x > y : x >= y;
    }
  }
  throw new DroidError(`Line ${line}: unknown operator ${op}`);
}

function builtin(name: string, args: Val[], st: VmState, line: number): Val {
  const n = (i: number): number => numOf(args[i] ?? null, name, line);
  switch (name) {
    case 'abs': return Math.abs(n(0));
    case 'min': case 'max': {
      const xs = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
      if (!xs.length) return null;
      const ns = xs.map((x) => numOf(x, name, line));
      return name === 'min' ? Math.min(...ns) : Math.max(...ns);
    }
    case 'floor': return Math.floor(n(0));
    case 'ceil': return Math.ceil(n(0));
    case 'round': return Math.round(n(0));
    case 'sqrt': { const x = n(0); if (x < 0) throw new DroidError(`Line ${line}: sqrt of a negative number`); return Math.sqrt(x); }
    case 'clamp': return Math.max(n(1), Math.min(n(2), n(0)));
    case 'len': { const v = args[0] ?? null; return typeof v === 'string' || Array.isArray(v) ? v.length : v && typeof v === 'object' ? Object.keys(v).length : 0; }
    case 'str': return capStr(showVal(args[0] ?? null));
    case 'num': { const v = args[0] ?? null; const x = typeof v === 'number' ? v : Number(v); return Number.isFinite(x) ? x : null; }
    case 'dist': {
      if (args.length === 4) return Math.hypot(n(0) - n(2), n(1) - n(3));
      const a = args[0], b = args[1];
      const px = (v: Val): [number, number] => {
        if (v && typeof v === 'object' && !Array.isArray(v)) return [numOf((v as Record<string, Val>).x ?? null, 'dist', line), numOf((v as Record<string, Val>).z ?? null, 'dist', line)];
        throw new DroidError(`Line ${line}: dist takes four numbers or two things with x/z`);
      };
      const [ax, az] = px(a ?? null), [bx, bz] = px(b ?? null);
      return Math.hypot(ax - bx, az - bz);
    }
    case 'range': { const k = Math.max(0, Math.min(LIMITS.list, Math.floor(n(0)))); return Array.from({ length: k }, (_, i) => i); }
    case 'contains': { const h = args[0] ?? null, v = args[1] ?? null; if (typeof h === 'string') return h.includes(showVal(v)); if (Array.isArray(h)) return h.some((x) => JSON.stringify(x) === JSON.stringify(v)); return false; }
    case 'push': { const l = args[0]; if (!Array.isArray(l)) throw new DroidError(`Line ${line}: push needs a list`); if (l.length >= LIMITS.list) throw new DroidError(`Line ${line}: lists hold at most ${LIMITS.list} items`); return capCells([...l, args[1] ?? null], line); }
    case 'join': { const l = args[0]; if (!Array.isArray(l)) return ''; return capStr(l.map(showVal).join(typeof args[1] === 'string' ? args[1] : ', ')); }
    case 'upper': return capStr(showVal(args[0] ?? null).toUpperCase());
    case 'lower': return capStr(showVal(args[0] ?? null).toLowerCase());
    case 'rand': {
      const k = Math.max(1, Math.floor(n(0)));
      let x = st.rng >>> 0; x ^= x << 13; x >>>= 0; x ^= x >>> 17; x ^= x << 5; x >>>= 0;
      st.rng = x || 1;
      return x % k;
    }
  }
  throw new DroidError(`Line ${line}: unknown function '${name}'`);
}

function lookup(st: VmState, name: string, line: number): Val {
  const f = st.frames[st.frames.length - 1];
  if (f && name in f.loc) return f.loc[name];
  if (name in st.g) return st.g[name];
  throw new DroidError(`Line ${line}: '${name}' has no value yet (use let ${name} = …)`);
}

function assign(st: VmState, name: string, v: Val, isLet: boolean, line: number): void {
  const f = st.frames[st.frames.length - 1];
  if (isLet) {
    const scope = f ? f.loc : st.g;
    if (!(name in scope) && Object.keys(scope).length >= LIMITS.vars) throw new DroidError(`Line ${line}: too many variables (${LIMITS.vars})`);
    scope[name] = v;
    return;
  }
  if (f && name in f.loc) { f.loc[name] = v; return; }
  if (name in st.g) { st.g[name] = v; return; }
  throw new DroidError(`Line ${line}: '${name}' was never set — write let ${name} = … first`);
}

/**
 * Run the program for `dt` seconds of droid time. Resumable: every bit of state is in `st`.
 *
 * Order each call: (1) the handlers are checked once — the first whose condition holds takes
 * over (the main program, and any walk it was on, is saved); (2) the pending wait is advanced
 * with the time left; (3) instructions run until a command yields a new wait, the step budget
 * runs out, or the program ends. A program whose main part is finished but which has handlers
 * PARKS on its last instruction and keeps answering its handlers.
 */
export function runDroid(prog: Program, st: VmState, host: DroidHost, dt: number): void {
  if (st.halt === 'done' || st.halt === 'error') return;
  if (st.halt === 'broke') st.halt = '';
  st.clock += dt;
  let left = dt;
  let budget = LIMITS.stepsPerTick;
  // 1. Handlers (checked once per tick, first match wins — the gambit list).
  if (st.h < 0) {
    for (let i = 0; i < prog.handlers.length; i++) {
      const h = prog.handlers[i];
      if (st.clock < (st.next[i] ?? 0)) continue;
      let fire = false;
      if (h.kind === 'every') fire = true;
      else {
        if (!host.charge(QUERY_COST)) { st.halt = 'broke'; return; }
        fire = host.event(h.event, h.arg);
      }
      if (!fire) continue;
      st.saved = { pc: st.pc, stack: st.stack, frames: st.frames, wait: st.wait };
      st.pc = h.addr; st.stack = []; st.frames = []; st.wait = null; st.h = i;
      if (h.kind === 'every') st.next[i] = st.clock + h.arg;
      break;
    }
  }
  const code = prog.code;
  try {
    for (;;) {
      // 2. The pending wait.
      if (st.wait) {
        const rem = host.advance(st.wait, left);
        if (rem < 0) return; // still going — the tick is spent
        st.wait = null;
        left = rem;
      }
      if (budget <= 0) return;
      // 3. Execute until a yield.
      let yielded = false;
      while (budget > 0 && !yielded) {
        const ins = code[st.pc];
        if (!ins) { st.halt = 'done'; return; }
        const line = prog.lines[st.pc] ?? 0;
        st.line = line;
        if (!host.charge(INSTR_COST)) { st.halt = 'broke'; return; }
        budget--; st.steps++;
        const op = ins[0];
        st.pc++;
        switch (op) {
          case 'push': st.stack.push((ins[1] ?? null) as Val); break;
          case 'get': st.stack.push(lookup(st, String(ins[1]), line)); break;
          case 'let': assign(st, String(ins[1]), st.stack.pop() ?? null, true, line); break;
          case 'set': assign(st, String(ins[1]), st.stack.pop() ?? null, false, line); break;
          case 'bin': { const b = st.stack.pop() ?? null, a = st.stack.pop() ?? null; st.stack.push(binop(String(ins[1]), a, b, line)); break; }
          case 'neg': st.stack.push(-numOf(st.stack.pop() ?? null, '-', line)); break;
          case 'not': st.stack.push(!truthy(st.stack.pop() ?? null)); break;
          case 'and': { const v = st.stack[st.stack.length - 1] ?? null; if (!truthy(v)) st.pc = Number(ins[1]); else st.stack.pop(); break; }
          case 'or': { const v = st.stack[st.stack.length - 1] ?? null; if (truthy(v)) st.pc = Number(ins[1]); else st.stack.pop(); break; }
          case 'jmp': st.pc = Number(ins[1]); break;
          case 'jf': if (!truthy(st.stack.pop() ?? null)) st.pc = Number(ins[1]); break;
          case 'pop': st.stack.pop(); break;
          case 'list': { const k = Number(ins[1]); const items = st.stack.splice(st.stack.length - k, k); st.stack.push(capCells(items, line)); break; }
          case 'fld': {
            const o = st.stack.pop() ?? null;
            const name = String(ins[1]);
            if (o === null) st.stack.push(null);
            else if (typeof o === 'object' && !Array.isArray(o)) st.stack.push((o as Record<string, Val>)[name] ?? null);
            else throw new DroidError(`Line ${line}: .${name} needs a record, got ${showVal(o)}`);
            break;
          }
          case 'idx': {
            const i = st.stack.pop() ?? null, o = st.stack.pop() ?? null;
            if (Array.isArray(o)) { const k = Math.floor(numOf(i, 'an index', line)); st.stack.push(o[k < 0 ? o.length + k : k] ?? null); }
            else if (typeof o === 'string') { const k = Math.floor(numOf(i, 'an index', line)); st.stack.push(o[k] ?? null); }
            else if (o && typeof o === 'object') st.stack.push((o as Record<string, Val>)[showVal(i)] ?? null);
            else st.stack.push(null);
            break;
          }
          case 'fn': {
            const name = String(ins[1]); const argc = Number(ins[2]);
            const local = name === 'ok' || name === 'result' || name === 'last' || name === 'clock' || name === 'cycles';
            if (QUERIES[name] && !local && !host.charge(QUERY_COST)) { st.pc--; st.halt = 'broke'; return; }
            const args = st.stack.splice(st.stack.length - argc, argc);
            if (QUERIES[name]) {
              if (name === 'ok') st.stack.push(st.ok);
              else if (name === 'result') st.stack.push(st.msg);
              else if (name === 'last') st.stack.push(st.last);
              else if (name === 'clock') st.stack.push(Math.round(st.clock * 10) / 10);
              else if (name === 'cycles') st.stack.push(st.cycles);
              else st.stack.push(host.query(name, args));
            } else st.stack.push(builtin(name, args, st, line));
            break;
          }
          case 'cmd': {
            const name = String(ins[1]); const argc = Number(ins[2]);
            const args = st.stack.splice(st.stack.length - argc, argc);
            const r = host.command(name, args, line);
            if (r.broke) { st.stack.push(...args); st.pc--; st.halt = 'broke'; return; }
            st.ok = r.ok; st.msg = capStr(r.msg ?? ''); st.last = r.value ?? null;
            if (r.wait) { st.wait = r.wait; yielded = true; }
            break;
          }
          case 'call': {
            if (st.frames.length >= LIMITS.frames) throw new DroidError(`Line ${line}: functions call each other too deeply (${LIMITS.frames})`);
            const addr = Number(ins[1]); const argc = Number(ins[2]);
            const args = st.stack.splice(st.stack.length - argc, argc);
            let params: string[] = [];
            for (const f of Object.values(prog.funcs)) if (f.addr === addr) { params = f.params; break; }
            const loc: Record<string, Val> = {};
            params.forEach((p, i) => { loc[p] = args[i] ?? null; });
            st.frames.push({ ret: st.pc, loc });
            st.pc = addr;
            break;
          }
          case 'ret': {
            const v = st.stack.pop() ?? null;
            const f = st.frames.pop();
            if (!f) throw new DroidError(`Line ${line}: return outside a function`);
            st.pc = f.ret;
            st.stack.push(v);
            break;
          }
          case 'tick': if (st.h < 0) st.cycles++; break;
          case 'loop': st.pc = 0; st.stack = []; break;
          case 'hend': {
            const sv = st.saved;
            const hi = st.h;
            st.h = -1; st.saved = null;
            st.pc = sv ? sv.pc : 0; st.stack = sv ? sv.stack : []; st.frames = sv ? sv.frames : []; st.wait = sv ? sv.wait : null;
            const hd = prog.handlers[hi];
            if (hd && hd.kind === 'on') st.next[hi] = st.clock + 1;
            yielded = true;
            break;
          }
          case 'stop': st.halt = 'done'; return;
          case 'halt':
            if (prog.handlers.length > 0) { st.pc--; return; } // parked: the handlers keep working
            st.halt = 'done';
            return;
          default: throw new DroidError(`Line ${line}: bad instruction ${op}`);
        }
        if (st.stack.length > LIMITS.stack) throw new DroidError(`Line ${line}: the program's stack overflowed`);
      }
      if (!yielded) return; // the step budget is spent
    }
  } catch (e) {
    if (e instanceof DroidError) { st.halt = 'error'; st.err = e.message; return; }
    st.halt = 'error';
    st.err = `Line ${st.line}: ${String((e as Error).message ?? e).slice(0, 160)}`;
  }
}

/** The line the program is on (for the terminal). */
export function currentLine(prog: Program, st: VmState): number {
  return prog.lines[Math.max(0, st.pc - (st.wait ? 1 : 0))] ?? st.line;
}

// ─── The estimator ─────────────────────────────────────────────────────────────────────────────

export interface EstimateLine { line: number; what: string; cost: number; secs: number; times: number }

export interface Estimate {
  ok: boolean;
  errors: string[];
  warnings: string[];
  cls: DroidClass;
  /** The hourly draw while running (credits). */
  runPerHour: number;
  /** One pass of the main program. */
  passCost: number;
  passSecs: number;
  loops: boolean;
  /** Handlers' credits per hour (assumed rates for events). */
  handlerPerHour: number;
  /** Everything together, per hour of running. */
  perHour: number;
  /** A one-shot program's total (a program that does not loop). */
  oneShotCost: number;
  /** Credits `upkeep` moves out of YOUR WALLET (not the pool) an hour, after the per-droid hourly cap (2026-09-28). */
  walletPerHour: number;
  /** The program would move more than the cap: the droid pays UPKEEP_HOUR_CAP an hour and the rest is refused. */
  walletCapped: boolean;
  lines: EstimateLine[];
  assumptions: string[];
}

export interface EstimateOpts {
  cls: DroidClass;
  quality?: number;
  homeX?: number;
  homeZ?: number;
  /** How many times an open-ended while / for runs (default 3). */
  loopGuess?: number;
  /** How often an 'on' handler fires per hour (default 12). */
  eventsPerHour?: number;
  /** Distance assumed for a goto to something found at run time (m, default 60). */
  moveGuessM?: number;
  /** Complexity of a craftable component (default 2). */
  complexityOf?: (key: string) => number;
}

/** A static cost estimate for a program on a class — the terminal's calculator. */
export function estimateDroid(source: string, o: EstimateOpts): Estimate {
  const quality = o.quality ?? 500;
  const stats = droidStats(o.cls, quality);
  const loopGuess = o.loopGuess ?? 3;
  const evPerHour = o.eventsPerHour ?? 12;
  const moveGuess = o.moveGuessM ?? 60;
  const empty: Estimate = {
    ok: false, errors: [], warnings: [], cls: o.cls, runPerHour: stats.runPerHour, passCost: 0, passSecs: 0,
    loops: false, handlerPerHour: 0, perHour: stats.runPerHour, oneShotCost: 0, walletPerHour: 0, walletCapped: false, lines: [], assumptions: [],
  };
  const cr = compileDroid(source, { cls: o.cls, homeX: o.homeX, homeZ: o.homeZ });
  if (!cr.ok || !cr.ast) return { ...empty, errors: cr.errors };
  const ast = cr.ast;
  const assumptions = new Set<string>();
  const byLine = new Map<number, EstimateLine>();
  const defs = new Map<string, Extract<Stmt, { k: 'def' }>>();
  for (const s of ast) if (s.k === 'def') defs.set(s.name, s);
  const home = { x: o.homeX ?? 0, z: o.homeZ ?? 0 };
  const cursor = { x: home.x, z: home.z, known: true };
  // UPKEEP IS WALLET MONEY (2026-09-28): what `upkeep` moves out of the owner's wallet, apart from the pool's cost —
  // per main pass (`walletMain`) and per hour of handlers (`walletHandlers`), selected by `walletInto`.
  let walletMain = 0, walletHandlers = 0;
  let walletInto: 'main' | 'handlers' = 'main';
  const note = (line: number, what: string, cost: number, secs: number, times: number): void => {
    const e = byLine.get(line) ?? { line, what, cost: 0, secs: 0, times: 0 };
    e.cost += cost * times; e.secs += secs * times; e.times += times;
    byLine.set(line, e);
  };
  const exprCost = (e: Expr | null, depth = 0): number => {
    if (!e) return 0;
    let c = INSTR_COST;
    switch (e.k) {
      case 'list': for (const it of e.items) c += exprCost(it, depth); break;
      case 'bin': c += exprCost(e.a, depth) + exprCost(e.b, depth); break;
      case 'un': c += exprCost(e.a, depth); break;
      case 'field': c += exprCost(e.obj, depth); break;
      case 'index': c += exprCost(e.obj, depth) + exprCost(e.idx, depth); break;
      case 'call':
        for (const a of e.args) c += exprCost(a, depth);
        if (QUERIES[e.name]) c += QUERY_COST;
        if (defs.has(e.name)) c += blockEst(defs.get(e.name)!.body, 1, depth + 1).cost;
        break;
    }
    return c;
  };
  const moveTo = (x: number, z: number): number => {
    const d = cursor.known ? Math.hypot(x - cursor.x, z - cursor.z) : moveGuess;
    cursor.x = x; cursor.z = z; cursor.known = true;
    return d;
  };
  const numLit = (e: Expr | undefined): number | null => (e && e.k === 'num' ? e.v : e && e.k === 'un' && e.op === '-' && e.a.k === 'num' ? -e.a.v : null);
  const cmdEst = (s: Extract<Stmt, { k: 'cmd' }>, times: number): { cost: number; secs: number } => {
    const spec = COMMANDS[s.name];
    if (!spec) return { cost: 0, secs: 0 };
    const a = s.args;
    let cost = 0, secs = typeof spec.secs === 'number' ? spec.secs : 0, dist = 0;
    for (const x of a) cost += exprCost(x);
    switch (s.name) {
      case 'goto': {
        const x = numLit(a[0]), z = numLit(a[1]);
        if (x !== null && z !== null) dist = moveTo(x, z);
        else { dist = moveGuess; cursor.known = false; assumptions.add(`a walk to something found at run time is taken as ${moveGuess} m`); }
        break;
      }
      case 'move': dist = numLit(a[1]) ?? 40; cursor.known = false; break;
      case 'route': {
        for (let i = 0; i + 1 < a.length; i += 2) {
          const x = numLit(a[i]), z = numLit(a[i + 1]);
          if (x !== null && z !== null) dist += moveTo(x, z);
          else { dist += moveGuess; cursor.known = false; }
        }
        break;
      }
      case 'patrol': { const r = numLit(a[0]) ?? 40; dist = 2 * Math.PI * r + r * 2; cursor.x = home.x; cursor.z = home.z; break; }
      case 'wander': { const w = numLit(a[1]) ?? 60; secs = w; dist = stats.speed * w * 0.5; break; }
      case 'home': dist = cursor.known ? Math.hypot(cursor.x - home.x, cursor.z - home.z) : moveGuess; cursor.x = home.x; cursor.z = home.z; cursor.known = true; break;
      case 'come': dist = 30; cursor.known = false; assumptions.add('walking to you is taken as 30 m'); break;
      case 'follow': { const w = numLit(a[0]) ?? 60; secs = w; dist = stats.speed * w * 0.3; cursor.known = false; break; }
      case 'wait': secs = numLit(a[0]) ?? 10; break;
      case 'survey': {
        const r = numLit(a[1]) ?? 150, step = numLit(a[2]) ?? 50;
        const pts = surveyPointCount(r, step);
        cost += pts * spec.cost * stats.opMult;
        dist = pts * step;
        secs = pts * 1;
        break;
      }
      case 'guard': {
        const w = numLit(a[1]) ?? 60;
        secs = w;
        const strikes = w / 6;
        cost += strikes * spec.cost * stats.opMult;
        assumptions.add('a guard post is taken to strike once every 6 s');
        break;
      }
      case 'upkeep': {
        cost += spec.cost * stats.opMult;
        const amt = Math.max(1, Math.min(UPKEEP_CALL_MAX, Math.floor(numLit(a[0]) ?? 100)));
        if (numLit(a[0]) === null) assumptions.add('an upkeep with a computed amount is taken as 100 credits');
        if (walletInto === 'main') walletMain += amt * times; else walletHandlers += amt * times;
        break;
      }
      case 'craft': {
        const n = numLit(a[1]) ?? 1;
        const key = a[0]?.k === 'str' ? a[0].v : '';
        const cx = o.complexityOf ? o.complexityOf(key) : 2;
        cost += n * (spec.cost + CRAFT_PER_COMPLEXITY * cx) * stats.opMult;
        secs = n * (spec.secs as number);
        break;
      }
      default:
        cost += spec.cost * stats.opMult;
    }
    if (dist > 0) {
      cost += dist * MOVE_COST_PER_M * stats.opMult;
      secs += dist / stats.speed;
    }
    note(s.line, `${s.name}`, cost, secs, times);
    return { cost, secs };
  };
  function blockEst(body: Stmt[], times: number, depth: number): { cost: number; secs: number; looped: boolean } {
    let cost = 0, secs = 0, looped = false;
    if (depth > 6) { assumptions.add('deep recursion is estimated only a few levels down'); return { cost, secs, looped }; }
    for (const s of body) {
      if (looped) break;
      switch (s.k) {
        case 'cmd': {
          if (defs.has(s.name)) { const r = blockEst(defs.get(s.name)!.body, times, depth + 1); cost += r.cost; secs += r.secs; for (const x of s.args) cost += exprCost(x, depth); break; }
          const r = cmdEst(s, times); cost += r.cost; secs += r.secs; break;
        }
        case 'let': case 'assign': { const c = exprCost(s.e, depth) + INSTR_COST; cost += c; note(s.line, s.k === 'let' ? `let ${s.name}` : `${s.name} ${s.op}`, c, 0, times); break; }
        case 'expr': { const c = exprCost(s.e, depth); cost += c; note(s.line, 'call', c, 0, times); break; }
        case 'if': {
          let worst = { cost: 0, secs: 0 };
          let cc = 0;
          for (const arm of s.arms) {
            cc += exprCost(arm.cond, depth);
            const r = blockEst(arm.body, times, depth + 1);
            if (r.cost > worst.cost) worst = r;
          }
          if (s.els) { const r = blockEst(s.els, times, depth + 1); if (r.cost > worst.cost) worst = r; }
          cost += cc + worst.cost; secs += worst.secs;
          if (s.arms.length > 1 || s.els) assumptions.add('an if/else is costed at its most expensive branch');
          break;
        }
        case 'while': case 'for': {
          assumptions.add(`an open-ended while/for loop is taken to run ${loopGuess} times`);
          const r = blockEst(s.body, times * loopGuess, depth + 1);
          cost += r.cost * loopGuess + (s.k === 'while' ? exprCost(s.cond, depth) : exprCost(s.list, depth)) * (loopGuess + 1);
          secs += r.secs * loopGuess;
          break;
        }
        case 'repeat': {
          const n = numLit(s.n);
          const k = n !== null ? Math.max(0, Math.floor(n)) : loopGuess;
          if (n === null) assumptions.add(`a repeat with a computed count is taken to run ${loopGuess} times`);
          const r = blockEst(s.body, times * k, depth + 1);
          cost += r.cost * k; secs += r.secs * k;
          break;
        }
        case 'forever': {
          const r = blockEst(s.body, times, depth + 1);
          cost += r.cost; secs += r.secs; looped = true;
          break;
        }
        case 'loop': looped = true; break;
        case 'stop': looped = false; return { cost, secs, looped: false };
        default: break;
      }
    }
    return { cost, secs, looped };
  }
  const main = ast.filter((s) => s.k !== 'def' && s.k !== 'on' && s.k !== 'every');
  const pass = blockEst(main, 1, 0);
  const loops = pass.looped || (cr.program?.loops ?? false);
  let handlerPerHour = 0;
  walletInto = 'handlers';
  for (const s of ast) {
    if (s.k === 'every') {
      const per = 3600 / s.secs;
      const r = blockEst(s.body, per, 1);
      handlerPerHour += r.cost * per;
    } else if (s.k === 'on') {
      assumptions.add(`an 'on' handler is taken to fire ${evPerHour} times an hour`);
      const r = blockEst(s.body, evPerHour, 1);
      handlerPerHour += r.cost * evPerHour;
    }
  }
  const passSecs = Math.max(pass.secs, loops ? 1 : 0);
  const loopPerHour = loops ? (3600 / Math.max(1, passSecs)) * pass.cost : 0;
  const perHour = stats.runPerHour + loopPerHour + handlerPerHour;
  // A looping program pays its pass's upkeep once a pass; a one-shot pays it once (inside its first hour).
  const walletRaw = (loops ? (3600 / Math.max(1, passSecs)) * walletMain : walletMain) + walletHandlers;
  const walletCapped = walletRaw > UPKEEP_HOUR_CAP;
  if (walletRaw > 0) assumptions.add(`upkeep pays from YOUR wallet, not the pool — at most ${UPKEEP_HOUR_CAP} credits an hour per droid`);
  return {
    ok: true, errors: [], warnings: cr.warnings, cls: o.cls, runPerHour: stats.runPerHour,
    passCost: pass.cost, passSecs, loops, handlerPerHour, perHour,
    oneShotCost: loops ? 0 : pass.cost + stats.runPerHour * pass.secs / 3600,
    walletPerHour: Math.min(UPKEEP_HOUR_CAP, walletRaw), walletCapped,
    lines: [...byLine.values()].sort((a, b) => a.line - b.line),
    assumptions: [...assumptions],
  };
}

/** Survey sample points inside a circle of radius r on a square grid of step s (capped at SURVEY_MAX_POINTS). */
export function surveyPointCount(r: number, step: number): number {
  return surveyGrid(0, 0, r, step).length;
}

/** The survey's walk: a serpentine over the grid points inside the circle, nearest row first. */
export function surveyGrid(cx: number, cz: number, r: number, step: number): Array<{ x: number; z: number }> {
  const s = Math.max(10, step);
  const n = Math.floor(r / s);
  const out: Array<{ x: number; z: number }> = [];
  for (let j = -n; j <= n; j++) {
    const row: Array<{ x: number; z: number }> = [];
    for (let i = -n; i <= n; i++) {
      if (Math.hypot(i * s, j * s) <= r + 1e-6) row.push({ x: cx + i * s, z: cz + j * s });
    }
    if ((j + n) % 2 === 1) row.reverse();
    out.push(...row);
    if (out.length >= SURVEY_MAX_POINTS) break;
  }
  return out.slice(0, SURVEY_MAX_POINTS);
}
/** A 64-point cap dropped the far rows of any survey past ~180 m at a 50 m step (2026-09-25); 121 covers 250 m. */
export const SURVEY_MAX_POINTS = 121;

/** The API as reference rows for the terminal (filtered by class when given). */
export function apiReference(cls?: DroidClass): Array<{ group: string; sig: string; doc: string; example: string; available: boolean; cost: string }> {
  const rows: Array<{ group: string; sig: string; doc: string; example: string; available: boolean; cost: string }> = [];
  const mult = cls ? droidStats(cls, 500).opMult : 1;
  for (const c of Object.values(COMMANDS)) {
    const sig = `${c.name} ${c.params.map((p) => (p.kind === 'nums' ? 'x z …' : p.opt ? `[${p.name}]` : p.name)).join(' ')}`.trim();
    const cost = c.name === 'survey' ? `${(c.cost * mult).toFixed(2)} a point + walking`
      : c.secs === 'move' ? `${(MOVE_COST_PER_M * mult * 100).toFixed(2)} per 100 m`
        : c.name === 'craft' ? `${(c.cost * mult).toFixed(1)} + ${(CRAFT_PER_COMPLEXITY * mult).toFixed(1)} × complexity each` : (c.cost * mult).toFixed(2);
    rows.push({ group: c.group, sig, doc: c.doc, example: c.example, available: cls ? classHas(cls, c.cap) : true, cost });
  }
  for (const q of Object.values(QUERIES)) {
    const sig = `${q.name}(${q.params.map((p) => (p.opt ? `[${p.name}]` : p.name)).join(', ')}) → ${q.returns}`;
    rows.push({ group: `Sensors: ${q.group}`, sig, doc: q.doc, example: '', available: cls ? (!q.cap || classHas(cls, q.cap)) : true, cost: (QUERY_COST * (['ok', 'result', 'last', 'clock', 'cycles'].includes(q.name) ? 0 : 1)).toFixed(2) });
  }
  for (const e of Object.values(EVENTS)) {
    rows.push({ group: 'Events (on …)', sig: `on ${e.name}${e.arg ? ` <${e.arg}>` : ''}`, doc: e.doc, example: '', available: true, cost: QUERY_COST.toFixed(2) + ' a check' });
  }
  for (const [k, v] of Object.entries(BUILTINS)) rows.push({ group: 'Maths & text', sig: v, doc: k, example: '', available: true, cost: '—' });
  return rows;
}

/** The longest a published report body may be (characters of JSON) — a report row is read by its owner's datapad. */
export const REPORT_JSON_MAX = 60_000;

/**
 * A report body as JSON of at most `max` characters that ALWAYS PARSES (2026-09-28): `JSON.stringify(body).slice(0,
 * 60000)` cut a long survey or a long note log mid-token, and the datapad's JSON.parse of that row threw. The largest
 * array is thinned from its end (halved) until the body fits, and the body says `truncated: true`; over-long strings
 * are cut; a body that still cannot fit becomes a short note saying so.
 */
export function fitReportJson(body: unknown, max = REPORT_JSON_MAX): string {
  let s = JSON.stringify(body) ?? 'null';
  if (s.length <= max) return s;
  let b = JSON.parse(s) as unknown;
  const mark = (): void => { if (b && typeof b === 'object' && !Array.isArray(b)) (b as Record<string, unknown>).truncated = true; };
  const arrays: unknown[][] = [];
  const strings: Array<{ o: Record<string, unknown> | unknown[]; k: string | number }> = [];
  const walk = (v: unknown, depth: number): void => {
    if (depth > 8 || !v || typeof v !== 'object') return;
    if (Array.isArray(v)) arrays.push(v);
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (typeof x === 'string') strings.push({ o: v as Record<string, unknown>, k: Array.isArray(v) ? Number(k) : k });
      else walk(x, depth + 1);
    }
  };
  walk(b, 0);
  for (const st of strings) {
    const o = st.o as Record<string | number, unknown>;
    const v = o[st.k];
    if (typeof v === 'string' && v.length > 2000) { o[st.k] = `${v.slice(0, 2000)}…`; mark(); }
  }
  s = JSON.stringify(b);
  for (let guard = 0; guard < 80 && s.length > max; guard++) {
    arrays.length = 0; strings.length = 0;
    walk(b, 0); // re-walked each pass: an array inside a halved-away element is no longer in the body
    let big: unknown[] | null = null, bigLen = 0;
    for (const a of arrays) { if (a.length === 0) continue; const n = JSON.stringify(a).length; if (n > bigLen) { big = a; bigLen = n; } }
    if (!big) break;
    big.length = Math.floor(big.length / 2);
    mark();
    s = JSON.stringify(b);
  }
  if (s.length <= max) return s;
  b = { truncated: true, note: 'the report was too large to keep' };
  return JSON.stringify(b);
}
